import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { legacyApi, EstimateSummary } from '../../../services/legacyApi';
import { CompactTable } from '../../../components/ui/CompactTable';
import { Button } from '../../../components/ui/button';
import { Plus, RotateCw, ArrowLeft } from 'lucide-react';
import { TableFilterBar } from '../../../components/table-filters/TableFilterBar';
import { useTableFilterState } from '../../../hooks/useTableFilterState';
import { getEstimateBaseNo, getEstimateRef } from '../../../utils/estimateRefs';

export const LegacyEstimateRevisions: React.FC = () => {
  const { id } = useParams();
  const estimateId = Number(id);
  const navigate = useNavigate();
  const [rows, setRows] = useState<EstimateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const { state, setFilter, clearAll, isDirty } = useTableFilterState({ q: '' });

  const load = () => {
    if (!estimateId) return;
    setLoading(true);
    legacyApi.estimates.listRevisions(estimateId)
      .then(setRows)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [estimateId]);

  const handleNewRevision = async () => {
    if (!estimateId) return;
    try {
      setBusy(true);
      const res = await legacyApi.estimates.revise(estimateId);
      navigate(`/legacy/estimates/${res.estimate_id}?tab=inputs`);
    } catch (err) {
      console.error('Failed to create revision', err);
    } finally {
      setBusy(false);
    }
  };

  const filteredRows = useMemo(() => {
    const query = state.q.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) =>
      `${row.estimate_id}`.includes(query) ||
      `${row.revision_no}`.includes(query) ||
      (row.customer_name || '').toLowerCase().includes(query) ||
      (row.project_type || '').toLowerCase().includes(query)
    );
  }, [rows, state.q]);
  if (loading) return <div className="p-8 text-center text-muted-foreground">Loading revisions...</div>;
  const rootEstimateRef = getEstimateBaseNo(filteredRows[0] || rows[0] || { estimate_id: estimateId });
  const formatAmount = (value?: number) =>
    Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const columns = [
    { header: 'Revision', accessor: (e: EstimateSummary) => e.revision_no.toString(), className: 'w-20' },
    { header: 'Reference', accessor: (e: EstimateSummary) => getEstimateRef(e) || e.estimate_id.toString(), className: 'w-36 font-mono' },
    { header: 'ID', accessor: (e: EstimateSummary) => e.estimate_id.toString(), className: 'w-20' },
    { header: 'Date', accessor: (e: EstimateSummary) => e.date_created ? new Date(e.date_created).toLocaleDateString() : '-', className: 'w-28' },
    { header: 'Customer', accessor: (e: EstimateSummary) => e.customer_name || '-', className: 'truncate' },
    { header: 'Project Type', accessor: (e: EstimateSummary) => e.project_type || '-', className: 'truncate' },
    { header: 'AVG Price / SQM', accessor: (e: EstimateSummary) => formatAmount(e.avg_price_per_sqm), className: 'w-28 text-right' },
    { header: 'Total Price', accessor: (e: EstimateSummary) => formatAmount(e.total_price), className: 'w-28 text-right' },
    { header: 'Net Profit', accessor: (e: EstimateSummary) => formatAmount(e.net_profit), className: 'w-28 text-right' },
    { header: 'Status', accessor: (e: EstimateSummary) => e.is_freezed ? 'Locked' : 'Open', className: 'w-16' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Revisions</h1>
          <p className="text-muted-foreground">{`Estimate ${rootEstimateRef || `HC/EST/${String(estimateId).padStart(4, '0')}`}`} - {rows.length} revision(s)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Button variant="outline" size="sm" onClick={load}>
            <RotateCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button size="sm" onClick={handleNewRevision} disabled={busy}>
            <Plus className="w-4 h-4 mr-2" /> New Revision
          </Button>
        </div>
      </div>

      <div className="min-h-[300px]">
        <TableFilterBar
          searchValue={state.q}
          onSearchChange={(value) => setFilter('q', value)}
          searchPlaceholder="Search revision, estimate ID, customer..."
          isDirty={isDirty}
          onClear={clearAll}
          rightSlot={`${filteredRows.length} results`}
        />
        <CompactTable
          data={filteredRows}
          columns={columns}
          keyField="estimate_id"
          onRowClick={(item) => {
            if (!item.is_freezed && item.approval_status_id !== 4) {
              navigate(`/legacy/estimates/${item.estimate_id}?tab=inputs`);
              return;
            }
            navigate(`/legacy/estimates/${item.estimate_id}`);
          }}
        />
      </div>
    </div>
  );
};
